import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { useAppData } from "@/data/AppDataContext";
import { useDataIsolation } from "@/hooks/useDataIsolation";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { BranchRegionState } from "@/components/BranchRegionFilter";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Users, Search, Briefcase, AlertTriangle, DollarSign, MapPin,
  Phone, ArrowUpRight, ArrowUpDown, ArrowUp, ArrowDown, Download, Globe,
} from "lucide-react";
import { useState, useMemo, useCallback } from "react";
import { QuickViews, QuickView } from "@/components/QuickViews";
import { BranchRegionFilter } from "@/components/BranchRegionFilter";

import { formatAddress } from "@/lib/addressUtils";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";

interface CustomerSummary {
  name: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  branch: string;
  region: string;
  jobCount: number;
  openJobCount: number;
  totalValue: number;
  openIssueCount: number;
  lastActivity: string;
  hasActiveJobs: boolean;
  hasCompletedOnly: boolean;
}

type SortField = "jobCount" | "totalValue" | "openIssueCount" | "lastActivity";
type SortDir = "asc" | "desc";

function SortIcon({ field, activeField, dir }: { field: SortField; activeField: SortField | null; dir: SortDir }) {
  if (activeField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
  return dir === "asc"
    ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
    : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
}

export default function CustomersList() {
  const { issues } = useAppData();
  const { jobs } = useDataIsolation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [activeView, setActiveView] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [brFilter, setBrFilter] = useState<BranchRegionState>({ branch: "all", region: "all" });
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const toggleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  }, [sortField]);

  const customers = useMemo(() => {
    const map = new Map<string, CustomerSummary>();
    for (const job of jobs) {
      const existing = map.get(job.customerName);
      const city = job.address.city || "";
      const isOpen = job.currentStage !== "Completed";
      const jobIssues = issues.filter(
        (i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress")
      );
      const lastAct =
        job.activityHistory.length > 0
          ? job.activityHistory[job.activityHistory.length - 1].date
          : job.scheduledInstallDate;

      if (existing) {
        existing.jobCount += 1;
        existing.openJobCount += isOpen ? 1 : 0;
        existing.totalValue += getJobDisplayValue(job);
        existing.openIssueCount += jobIssues.length;
        if (lastAct > existing.lastActivity) existing.lastActivity = lastAct;
        if (!existing.city && city) existing.city = city;
        if (isOpen) existing.hasActiveJobs = true;
        if (!isOpen && !existing.hasActiveJobs) existing.hasCompletedOnly = true;
      } else {
        map.set(job.customerName, {
          name: job.customerName,
          phone: job.phone,
          email: job.email || "",
          address: formatAddress(job.address),
          city,
          branch: job.branch,
          region: job.region,
          jobCount: 1,
          openJobCount: isOpen ? 1 : 0,
          totalValue: getJobDisplayValue(job),
          openIssueCount: jobIssues.length,
          lastActivity: lastAct,
          hasActiveJobs: isOpen,
          hasCompletedOnly: !isOpen,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.lastActivity.localeCompare(a.lastActivity));
  }, [jobs, issues]);

  const cities = useMemo(() => {
    const set = new Set<string>();
    customers.forEach((c) => { if (c.city) set.add(c.city); });
    return Array.from(set).sort();
  }, [customers]);

  const recentCutoff = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  }, []);

  const withIssues = customers.filter((c) => c.openIssueCount > 0).length;
  const withOpenJobs = customers.filter((c) => c.openJobCount > 0).length;
  const recentActivity = customers.filter((c) => c.lastActivity >= recentCutoff).length;
  const completedOnly = customers.filter((c) => c.hasCompletedOnly && !c.hasActiveJobs).length;

  const quickViews: QuickView[] = [
    { key: "all", label: "All Customers", count: customers.length },
    { key: "active", label: "Active Jobs", count: withOpenJobs, accent: "info" },
    { key: "issues", label: "Open Issues", count: withIssues, accent: "destructive" },
    { key: "recent", label: "Recent Activity", count: recentActivity, accent: "success" },
    { key: "completed", label: "Completed Only", count: completedOnly },
  ];

  const filtered = useMemo(() => {
    let list = customers;

    if (activeView === "active") list = list.filter((c) => c.openJobCount > 0);
    else if (activeView === "issues") list = list.filter((c) => c.openIssueCount > 0);
    else if (activeView === "recent") list = list.filter((c) => c.lastActivity >= recentCutoff);
    else if (activeView === "completed") list = list.filter((c) => c.hasCompletedOnly && !c.hasActiveJobs);

    if (cityFilter !== "all") list = list.filter((c) => c.city === cityFilter);
    if (brFilter.branch !== "all") list = list.filter((c) => c.branch === brFilter.branch);
    if (brFilter.region !== "all") list = list.filter((c) => c.region === brFilter.region);

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.city.toLowerCase().includes(q) ||
          c.phone.includes(q) ||
          c.email.toLowerCase().includes(q) ||
          c.address.toLowerCase().includes(q)
      );
    }

    if (sortField) {
      list = [...list].sort((a, b) => {
        let cmp = 0;
        if (sortField === "lastActivity") {
          cmp = a.lastActivity.localeCompare(b.lastActivity);
        } else {
          cmp = a[sortField] - b[sortField];
        }
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    return list;
  }, [customers, activeView, cityFilter, brFilter, search, recentCutoff, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safeP = Math.min(page, totalPages);
  const paginated = filtered.slice((safeP - 1) * PAGE_SIZE, safeP * PAGE_SIZE);

  // Reset page on filter change
  const setActiveViewAndReset = (v: string) => { setActiveView(v); setPage(1); };
  const setSearchAndReset = (v: string) => { setSearch(v); setPage(1); };

  const totalValue = customers.reduce((s, c) => s + c.totalValue, 0);
  const totalOpen = customers.reduce((s, c) => s + c.openJobCount, 0);
  const totalIssues = customers.reduce((s, c) => s + c.openIssueCount, 0);

  const exportCSV = useCallback(() => {
    const headers = ["Name", "Phone", "Email", "Address", "City", "Branch", "Region", "Jobs", "Open Jobs", "Open Issues", "Total Value", "Last Activity"];
    const rows = filtered.map((c) => [
      c.name, c.phone, c.email, c.address, c.city, c.branch, c.region,
      c.jobCount, c.openJobCount, c.openIssueCount, c.totalValue, c.lastActivity,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered]);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-lg font-bold tracking-tight flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Customers
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {customers.length} customers · {totalOpen} open jobs · ${totalValue.toLocaleString()} total value
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5" onClick={exportCSV}>
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
      </div>


      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Customers", value: customers.length, icon: Users },
          { label: "Open Jobs", value: totalOpen, icon: Briefcase },
          { label: "Open Issues", value: totalIssues, icon: AlertTriangle },
          { label: "Total Value", value: `$${totalValue.toLocaleString()}`, icon: DollarSign },
        ].map((kpi) => (
          <Card key={kpi.label} className="shadow-none">
            <CardContent className="p-3 text-center">
              <kpi.icon className="h-4 w-4 text-muted-foreground mx-auto mb-1" />
              <p className="text-lg font-bold">{kpi.value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <QuickViews views={quickViews} active={activeView} onChange={setActiveViewAndReset} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email, address..."
            value={search}
            onChange={(e) => setSearchAndReset(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>
        <Select value={cityFilter} onValueChange={setCityFilter}>
          <SelectTrigger className="h-9 text-xs w-[140px]">
            <SelectValue placeholder="City" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Cities</SelectItem>
            {cities.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <BranchRegionFilter value={brFilter} onChange={setBrFilter} jobs={jobs} />
      </div>

      {(activeView !== "all" || cityFilter !== "all" || brFilter.branch !== "all" || brFilter.region !== "all" || search) && (
        <p className="text-[11px] text-muted-foreground">
          Showing {filtered.length} of {customers.length} customers
        </p>
      )}

      <Card className="shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px] font-semibold">Customer</TableHead>
                <TableHead className="text-[11px] font-semibold hidden sm:table-cell">Phone</TableHead>
                <TableHead className="text-[11px] font-semibold hidden lg:table-cell">Email</TableHead>
                <TableHead className="text-[11px] font-semibold hidden md:table-cell">City / Area</TableHead>
                <TableHead
                  className="text-[11px] font-semibold text-center cursor-pointer select-none hover:text-primary transition-colors"
                  onClick={() => toggleSort("jobCount")}
                >
                  <span className="inline-flex items-center">Jobs <SortIcon field="jobCount" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead className="text-[11px] font-semibold text-center hidden sm:table-cell">Open</TableHead>
                <TableHead
                  className="text-[11px] font-semibold text-center hidden sm:table-cell cursor-pointer select-none hover:text-primary transition-colors"
                  onClick={() => toggleSort("openIssueCount")}
                >
                  <span className="inline-flex items-center">Issues <SortIcon field="openIssueCount" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead
                  className="text-[11px] font-semibold text-right hidden md:table-cell cursor-pointer select-none hover:text-primary transition-colors"
                  onClick={() => toggleSort("totalValue")}
                >
                  <span className="inline-flex items-center justify-end">Value <SortIcon field="totalValue" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead
                  className="text-[11px] font-semibold text-right hidden lg:table-cell cursor-pointer select-none hover:text-primary transition-colors"
                  onClick={() => toggleSort("lastActivity")}
                >
                  <span className="inline-flex items-center justify-end">Last Activity <SortIcon field="lastActivity" activeField={sortField} dir={sortDir} /></span>
                </TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginated.map((c) => (
                <TableRow key={c.name} className="group">
                  <TableCell className="py-2.5">
                    <Link to={`/customers/${encodeURIComponent(c.name)}`} className="font-semibold text-xs text-foreground hover:text-primary transition-colors">
                      {c.name}
                    </Link>
                  </TableCell>
                  <TableCell className="py-2.5 hidden sm:table-cell">
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <Phone className="h-3 w-3" /> {c.phone}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 hidden lg:table-cell">
                    <span className="text-xs text-muted-foreground">{c.email || "—"}</span>
                  </TableCell>
                  <TableCell className="py-2.5 hidden md:table-cell">
                    <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> {c.city}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 text-center">
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{c.jobCount}</Badge>
                  </TableCell>
                  <TableCell className="py-2.5 text-center hidden sm:table-cell">
                    {c.openJobCount > 0 ? (
                      <Badge className="text-[10px] px-1.5 py-0 bg-primary/10 text-primary border-primary/20">{c.openJobCount}</Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-center hidden sm:table-cell">
                    {c.openIssueCount > 0 ? (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{c.openIssueCount}</Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-right hidden md:table-cell">
                    <span className="text-xs font-medium">${c.totalValue.toLocaleString()}</span>
                  </TableCell>
                  <TableCell className="py-2.5 text-right hidden lg:table-cell">
                    <span className="text-[10px] text-muted-foreground">{c.lastActivity}</span>
                  </TableCell>
                  <TableCell className="py-2.5">
                    <Link to={`/customers/${encodeURIComponent(c.name)}`}>
                      <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="p-0">
                    <EmptyState
                      icon={Users}
                      title="No customers yet"
                      description="Customers appear here automatically once a lead is created or a job is sold."
                      actionLabel="Create Opportunity"
                      onAction={() => navigate("/opportunities")}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[11px] text-muted-foreground">
            Showing {(safeP - 1) * PAGE_SIZE + 1}–{Math.min(safeP * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="h-7 text-xs px-2.5" disabled={safeP <= 1} onClick={() => setPage(safeP - 1)}>
              Previous
            </Button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safeP) <= 1)
              .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((p, idx) =>
                p === "..." ? (
                  <span key={`e${idx}`} className="px-1 text-xs text-muted-foreground">…</span>
                ) : (
                  <Button
                    key={p}
                    variant={p === safeP ? "default" : "outline"}
                    size="sm"
                    className="h-7 w-7 text-xs p-0"
                    onClick={() => setPage(p as number)}
                  >
                    {p}
                  </Button>
                )
              )}
            <Button variant="outline" size="sm" className="h-7 text-xs px-2.5" disabled={safeP >= totalPages} onClick={() => setPage(safeP + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
